// Terrain-dressed text boxes (TT-1046, art team note): a text box should
// belong to the part of the scene it landed in — a cloud in the sky, a
// boulder with grass and moss on the ground, something afloat on water, a
// dug-out with roots underground, a leafy clump in the canopy. Everything
// here is path data in the box's own coordinates, so it resizes with the box
// (board chunks grow) and rides the same ink-draw animation and hand wobble
// as the plain box it replaces.
//
// Pure path data, no DOM. `rand` is any seeded () => [0, 1) so a box's
// dressing is stable across re-solves and redraws. Neutral terrain returns
// null / [] everywhere — callers keep their plain box.
//
// The contract: tint + trim always; the silhouette only where the box has no
// register of its own. The board's chunks are all one sketch rect, so terrain
// takes their shape; the scene's containers already mean something by shape
// (a banner is an idea, a cloud a tension), so they keep it. Trim stays
// sparse, and the board's seeded coin leaves some boxes plain, the way a
// recorder decorates a few frames, not all.

import { pointInPoly } from './packer.js';

// Fill = the wash inside the text box. trim = its strokes (grass, ripples,
// roots, leaves). moss = the wash streaks at a ground box's foot — a few
// greens, picked per streak (browns were tried and read as dirt smudges).
export const TERRAIN_TINT = {
  sky: { fill: '#eef5fb', trim: '#8fb4d6' },
  ground: { fill: '#f7f1e3', trim: '#6f9a4a', moss: ['#8fbf62', '#6fa64c', '#a4c46a'] },
  water: { fill: '#e8f4f6', trim: '#4d9bb0' },
  underground: { fill: '#f3eadb', trim: '#8a6a44' },
  canopy: { fill: '#eef6e3', trim: '#6a9f48' },
};

const DRESS_ODDS = 0.78;

/** The wash inside a dressed text box, or `fallback` for a plain one. */
export function terrainFill(dress, fallback) {
  return TERRAIN_TINT[dress]?.fill ?? fallback;
}

/**
 * The board's coin: a dressable terrain about DRESS_ODDS of the time, else
 * null for a plain box. Spends exactly one draw of `rand`, so key it off a
 * dedicated seed.
 */
export function terrainDressing(terrain, rand) {
  return TERRAIN_TINT[terrain] && rand() < DRESS_ODDS ? terrain : null;
}

/**
 * The terrain a rect's centre landed in, from regions ({ poly, terrain }):
 * the first region containing it, else neutral. The board dresses by where
 * a box actually LANDED, not the region it was assigned — a chunk the
 * packer spilled out of a full slope region into the sky is a cloud.
 */
export function terrainAt(rect, regions) {
  const c = [rect.x + rect.w / 2, rect.y + rect.h / 2];
  return regions.find((g) => g.poly && pointInPoly(c, g.poly))?.terrain || 'neutral';
}

// path-data formatting and the hand's jitter, shared with motifs.js
export const f = (n) => Number(n.toFixed(1));
export const pt = (x, y) => `${f(x)},${f(y)}`;

/** Scallop size for a cloud / leafy ring — small enough to stay inside the margin. */
export function scallop(r) {
  return Math.max(14, Math.min(26, Math.min(r.w, r.h) * 0.2));
}

/**
 * A ring of bumps around the rect (cloud). `pointy` alternates the bump
 * size and pinches the joins so it reads as leaves, not vapour.
 */
function ringPath(r, rand, pointy = false) {
  const { x, y, w, h } = r;
  const bump = scallop(r);
  const pts = [];
  const nTop = Math.max(3, Math.round(w / (bump * 1.6)));
  const nSide = Math.max(1, Math.round(h / (bump * 1.6)));
  for (let i = 0; i <= nTop; i++) pts.push([x + (w * i) / nTop, y]);
  for (let i = 1; i <= nSide; i++) pts.push([x + w, y + (h * i) / nSide]);
  for (let i = nTop - 1; i >= 0; i--) pts.push([x + (w * i) / nTop, y + h]);
  for (let i = nSide - 1; i >= 1; i--) pts.push([x, y + (h * i) / nSide]);
  let d = `M${pt(pts[0][0], pts[0][1])}`;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.hypot(dx, dy) || 1;
    const nx = dy / len; // outward normal (clockwise walk on screen)
    const ny = -dx / len;
    const s = bump * (pointy ? (i % 2 ? 0.55 : 1.15) : 0.8 + rand() * 0.4);
    if (pointy) {
      // two quads meeting at a tip → a leaf edge
      const mx = (a[0] + b[0]) / 2 + nx * s;
      const my = (a[1] + b[1]) / 2 + ny * s;
      d += ` Q${pt(a[0] + nx * s * 0.6, a[1] + ny * s * 0.6)} ${pt(mx, my)} Q${pt(b[0] + nx * s * 0.6, b[1] + ny * s * 0.6)} ${pt(b[0], b[1])}`;
    } else {
      d += ` Q${pt((a[0] + b[0]) / 2 + nx * s, (a[1] + b[1]) / 2 + ny * s)} ${pt(b[0], b[1])}`;
    }
  }
  return d + 'Z';
}

/** The hand's jitter: ± `wobble` units. */
export const jitter = (rand, wobble) => (rand() - 0.5) * wobble * 2;

/**
 * Points along straight segments [x0, y0, x1, y1, n, wobble?], each jittered
 * — the raw material of every sketched edge. `walk` joins them as `M…L…`.
 */
export function walkPoints(segments, rand, wobble) {
  const pts = [];
  for (const [x0, y0, x1, y1, n, wb] of segments) {
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      pts.push([x0 + (x1 - x0) * t + jitter(rand, wb ?? wobble), y0 + (y1 - y0) * t + jitter(rand, wb ?? wobble)]);
    }
  }
  return pts;
}

export function walk(segments, rand, wobble, first = 'M') {
  return walkPoints(segments, rand, wobble).map(([x, y], i) => `${i ? 'L' : first}${pt(x, y)}`).join('');
}

/**
 * Ground: a boulder that SITS. The bottom edge is straight and steady, the
 * top a run of low humps; all four corners are rounded — square bottom
 * corners read as a tombstone.
 */
function boulderPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const sh = Math.min(h * 0.4, w * 0.22, 26); // shoulder radius
  const fr = Math.min(h * 0.25, w * 0.15, 16); // foot radius
  const humps = Math.max(2, Math.round(w / Math.max(90, h * 1.3)));
  const j = () => jitter(rand, wobble);
  let d = `M${pt(x + fr + j(), y + h + j() * 0.3)}`; // bottom-left, after the foot
  d += ` Q${pt(x, y + h)} ${pt(x + j(), y + h - fr + j())}`; // left foot
  d += ` L${pt(x + j(), y + sh + j())}`; // up the left side
  d += ` Q${pt(x, y)} ${pt(x + sh + j(), y + j())}`; // left shoulder
  const x0 = x + sh;
  const x1 = x + w - sh;
  for (let i = 0; i < humps; i++) {
    const ax = x0 + ((x1 - x0) * (i + 1)) / humps;
    const mx = x0 + ((x1 - x0) * (i + 0.5)) / humps;
    d += ` Q${pt(mx + j(), y - (3 + rand() * 4))} ${pt(ax + j(), y + j())}`;
  }
  d += ` Q${pt(x + w, y)} ${pt(x + w + j(), y + sh + j())}`; // right shoulder
  d += ` L${pt(x + w + j(), y + h - fr + j())}`;
  d += ` Q${pt(x + w, y + h)} ${pt(x + w - fr + j(), y + h + j() * 0.3)}`; // right foot
  // bottom edge back to the start: steady, one midpoint, barely any wobble
  d += ` L${pt(x + w * 0.5 + j() * 0.3, y + h + j() * 0.4)}`;
  return d + 'Z';
}

/** Water: a plain top, and a bottom edge that waves like a waterline. */
function floatPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const rad = Math.min(14, w / 4, h / 4);
  const j = () => jitter(rand, wobble);
  let d = walk(
    [
      [x + rad, y, x + w - rad, y, 10],
      [x + w, y + rad, x + w, y + h - 8, 5],
    ],
    rand,
    wobble
  );
  const waves = Math.max(2, Math.round(w / 44));
  const amp = Math.min(6, h * 0.08);
  for (let i = 0; i < waves; i++) {
    const bx = x + w - ((w * (i + 1)) / waves);
    const mx = x + w - ((w * (i + 0.5)) / waves);
    d += ` Q${pt(mx + j(), y + h + amp * (i % 2 ? -1 : 1) * 1.6)} ${pt(bx + j(), y + h + j() * 0.4)}`;
  }
  d += ` L${pt(x + j(), y + rad + j())}`;
  return d + 'Z';
}

/**
 * Underground: the top edge is the surface — dead straight, a touch heavier
 * (callers may stroke it twice) — while the sides and floor are rough,
 * dug out with a couple of notches.
 */
function dugoutPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const j = () => jitter(rand, wobble);
  const rough = wobble * 2.2;
  let d = walk([[x, y, x + w, y, 8, 0.6]], rand, wobble); // surface
  d += walk([[x + w, y + 4, x + w + 2, y + h - 6, 5, rough]], rand, wobble, ' L');
  // floor with two notches
  const n1 = x + w * (0.62 + rand() * 0.1);
  const n2 = x + w * (0.28 + rand() * 0.1);
  d += ` L${pt(x + w - 4 + j(), y + h + j())} L${pt(n1 + 6, y + h + j())} L${pt(n1, y + h + 5 + rand() * 4)} L${pt(n1 - 7, y + h + j())}`;
  d += ` L${pt(n2 + 6, y + h + j())} L${pt(n2, y + h + 4 + rand() * 4)} L${pt(n2 - 6, y + h + j())} L${pt(x + 3 + j(), y + h + j())}`;
  d += walk([[x - 2, y + h - 6, x, y + 4, 5, rough]], rand, wobble, ' L');
  return d + 'Z';
}

/**
 * The text box's silhouette for a terrain, closed path data in the rect's
 * coordinates. `wobble` is the hand's jitter in units (the board passes its
 * own; the scene adds a displacement filter and passes ~1).
 */
export function terrainOutline(terrain, r, rand, wobble = 2.2) {
  switch (terrain) {
    case 'sky':
      return ringPath(r, rand, false);
    case 'canopy':
      return ringPath(r, rand, true);
    case 'ground':
      return boulderPath(r, rand, wobble);
    case 'water':
      return floatPath(r, rand, wobble);
    case 'underground':
      return dugoutPath(r, rand, wobble);
    default:
      return null;
  }
}

/**
 * Trim: the few strokes that tie the text box to its terrain, drawn AFTER
 * the outline (they may cross it). Each item: { d, stroke, fill,
 * strokeWidth, opacity, kind: 'stroke' | 'wash' }. A wash is a soft filled
 * blob (callers may blur it).
 */
export function terrainTrim(terrain, r, rand) {
  const { x, y, w, h } = r;
  const tint = TERRAIN_TINT[terrain];
  if (!tint) return [];
  const out = [];
  const stroke = (d, sw = 1.6, opacity = 0.85) => out.push({ d, stroke: tint.trim, fill: 'none', strokeWidth: sw, opacity, kind: 'stroke' });
  switch (terrain) {
    case 'ground': {
      // grass: a tuft every ~70 units along the foot, 4–5 blades each,
      // sprouting UP past the bottom edge; a few single blades between
      const tufts = Math.max(2, Math.min(7, Math.round(w / 70)));
      const blade = (px, py, s, lean) => `M${pt(px, py)} L${pt(px + lean * s * 0.5, py - s)}`;
      for (let i = 0; i < tufts; i++) {
        const px = x + w * ((i + 0.5) / tufts) + (rand() - 0.5) * w * 0.2;
        const py = y + h + 2;
        const s = 11 + rand() * 7;
        const blades = [blade(px, py, s, -0.9), blade(px + 3, py, s * 1.25, -0.15), blade(px + 6, py, s * 1.1, 0.25), blade(px + 9, py, s * 0.85, 0.95)];
        if (rand() < 0.6) blades.push(blade(px - 3, py, s * 0.7, -0.5));
        stroke(blades.join(' '), 2.2, 0.9);
        if (rand() < 0.5) stroke(blade(px + 16 + rand() * 20, py, 6 + rand() * 5, rand() - 0.5), 1.8, 0.7);
      }
      // moss: 2–3 long, thin streaks along the foot, half under the box,
      // each its own green; the first sits at a corner
      const blobs = 2 + (rand() < 0.5 ? 1 : 0);
      const colours = tint.moss;
      for (let i = 0; i < blobs; i++) {
        const left = i === 0 ? rand() < 0.5 : null;
        const rx = Math.min(48, w * 0.3) * (0.7 + rand() * 0.6);
        const ry = Math.min(5, h * 0.08) * (0.7 + rand() * 0.5);
        const mx = i === 0 ? (left ? x + rx * 0.4 + rand() * 10 : x + w - rx * 0.4 - rand() * 10) : x + w * (0.2 + rand() * 0.6);
        const c = colours[Math.floor(rand() * colours.length)];
        out.push({ d: `M${pt(mx - rx, y + h + rand() * 3)} a${f(rx)},${f(ry)} 0 1,0 ${f(rx * 2)},0 a${f(rx)},${f(ry)} 0 1,0 ${f(-rx * 2)},0Z`, stroke: 'none', fill: c, strokeWidth: 0, opacity: 0.4 + rand() * 0.2, kind: 'wash' });
      }
      break;
    }
    case 'water': {
      // two ripples under the waterline, offset like a reflection
      const r1 = w * (0.12 + rand() * 0.1);
      const l1 = w * (0.3 + rand() * 0.2);
      stroke(`M${pt(x + r1, y + h + 7)} q${f(l1 / 4)},-4 ${f(l1 / 2)},0 t${f(l1 / 2)},0`, 2, 0.8);
      const r2 = w * (0.45 + rand() * 0.2);
      const l2 = w * (0.2 + rand() * 0.15);
      stroke(`M${pt(x + r2, y + h + 13)} q${f(l2 / 4)},-4 ${f(l2 / 2)},0 t${f(l2 / 2)},0`, 1.8, 0.65);
      break;
    }
    case 'underground': {
      // roots: 2–3 forked threads hanging from the floor
      const n = 2 + (rand() < 0.5 ? 1 : 0);
      for (let i = 0; i < n; i++) {
        const px = x + w * ((i + 0.5) / n) + (rand() - 0.5) * w * 0.25;
        const py = y + h;
        const len = 12 + rand() * 7;
        const dx = (rand() - 0.5) * 8;
        stroke(`M${pt(px, py)} q${f(dx)},${f(len * 0.5)} ${f(dx * 1.6)},${f(len)} M${pt(px + dx * 0.8, py + len * 0.5)} l${f(5 + rand() * 4)},${f(5 + rand() * 4)}`, 1.8, 0.85);
      }
      break;
    }
    case 'canopy': {
      // two small leaves on the crown, filled
      for (let i = 0; i < 2; i++) {
        const px = x + w * (0.2 + rand() * 0.6);
        const py = y - 4 - rand() * 4;
        const s = 5 + rand() * 3;
        const dir = rand() < 0.5 ? -1 : 1;
        out.push({ d: `M${pt(px, py)} q${f(dir * s)},${f(-s * 1.4)} ${f(dir * s * 1.8)},${f(-s * 0.6)} q${f(-dir * s * 0.4)},${f(s * 1.2)} ${f(-dir * s * 1.8)},${f(s * 0.6)}Z`, stroke: tint.trim, fill: tint.trim, strokeWidth: 1, opacity: 0.55, kind: 'stroke' });
      }
      break;
    }
    case 'sky': {
      // one small detached puff off a top corner, sometimes
      if (rand() < 0.5) break;
      const left = rand() < 0.5;
      const px = left ? x - 6 : x + w - 18;
      const py = y - 8;
      stroke(`M${pt(px, py + 6)} q3,-7 10,-5 q4,-6 12,-1 q6,-2 6,5`, 1.5, 0.6);
      break;
    }
    default:
      break;
  }
  return out;
}
